import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Motion";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé. À très vite !");
      setForm({ nom: "", email: "", telephone: "", message: "" });
    } catch (err) {
      toast.error("Erreur d'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <SEO
        title="Contact chapiste Mulhouse — Euro Chape à Illzach"
        description="Contactez Euro Chape, artisan chapiste à Illzach près de Mulhouse. Téléphone +33 7 67 94 54 59, email eurochape68@gmail.com. Intervention en Alsace et Territoire de Belfort."
        keywords="contact chapiste Mulhouse, contact Euro Chape, artisan sols Alsace, chape Illzach contact"
        canonical="https://eurochape.com/contact"
      />
      <section className="pt-32 md:pt-40 pb-12 px-6 md:px-12 lg:px-24 bg-[#EBEBE6] border-b border-[#D5D5D0]">
        <Reveal>
          <SectionLabel>Contact</SectionLabel>
          <h1 className="font-display font-black uppercase tracking-tighter mt-8 text-5xl sm:text-7xl lg:text-[7rem] leading-[0.88]">
            Parlons
            <br />
            <span className="text-[#F08722]">sols.</span>
          </h1>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <h2 className="font-display font-bold uppercase tracking-tighter text-3xl sm:text-4xl leading-[1] mb-10">
              Coordonnées
            </h2>
            <div className="space-y-8">
              <ContactBlock
                icon={Phone}
                label="Téléphone"
                value="07 67 94 54 59"
                href="tel:+33767945459"
                testid="contact-phone"
              />
              <ContactBlock
                icon={Mail}
                label="Email"
                value="eurochape68@gmail.com"
                href="mailto:eurochape68@gmail.com"
                testid="contact-email"
              />
              <ContactBlock
                icon={MapPin}
                label="Adresse"
                value={"1 rue de Habsheim\n68110 Illzach"}
                testid="contact-address"
              />
              <ContactBlock
                icon={Clock}
                label="Horaires"
                value={"Lun – Ven : 8h – 18h\nSam : sur rendez-vous"}
              />
            </div>

            <div className="mt-12 p-6 border border-[#D5D5D0] bg-[#EBEBE6]">
              <div className="text-xs uppercase tracking-[0.22em] text-[#5C5E58] mb-2">
                Zone d'intervention
              </div>
              <div className="font-display font-bold text-xl">
                Alsace &amp; Territoire de Belfort
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-[#D5D5D0] p-8 md:p-12 space-y-8"
            >
              <h2 className="font-display font-bold uppercase tracking-tighter text-2xl sm:text-3xl">
                Envoyer un message
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Nom *"
                  value={form.nom}
                  onChange={setField("nom")}
                  testid="contact-field-nom"
                />
                <Field
                  type="email"
                  label="Email *"
                  value={form.email}
                  onChange={setField("email")}
                  testid="contact-field-email"
                />
              </div>
              <Field
                type="tel"
                label="Téléphone"
                value={form.telephone}
                onChange={setField("telephone")}
                testid="contact-field-telephone"
              />
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-[#5C5E58] mb-2">
                  Message *
                </span>
                <textarea
                  value={form.message}
                  onChange={setField("message")}
                  rows={5}
                  data-testid="contact-field-message"
                  className="w-full bg-transparent border-b-2 border-[#D5D5D0] focus:border-[#F08722] focus:outline-none rounded-none px-0 py-3 text-base text-[#1A2D7E] resize-none"
                />
              </label>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={submitting}
                className="inline-flex items-center gap-3 bg-[#1A2D7E] text-white px-10 py-5 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#F08722] transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
                  </>
                ) : (
                  <>
                    Envoyer <ArrowUpRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-[#5C5E58] mb-2">
        {label}
      </span>
      <input
        {...props}
        data-testid={testid}
        className="w-full bg-transparent border-b-2 border-[#D5D5D0] focus:border-[#F08722] focus:outline-none rounded-none px-0 py-3 text-base text-[#1A2D7E]"
      />
    </label>
  );
}

function ContactBlock({ icon: Icon, label, value, href, testid }) {
  const content = (
    <>
      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#5C5E58]">
        <Icon className="w-4 h-4 text-[#F08722]" />
        {label}
      </div>
      <div className="font-display font-bold text-xl md:text-2xl mt-2 tracking-tight whitespace-pre-line">
        {value}
      </div>
    </>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block link-underline">
      {content}
    </a>
  ) : (
    <div data-testid={testid}>{content}</div>
  );
}
